// Runtime validation for database objects

export function validateFieldsMatch(expectedFields: any[], data: any): string[] {
  const errors: string[] = [];
  for (const field of expectedFields)
    if (!(field in data))
      errors.push(`Missing required field: ${field}`);

  for (const field in data)
    if (!expectedFields.includes(field))
      errors.push(`Unexpected field: ${field}`);

  return errors;
}

export function validateUserProfile(data: any): void {
  const errors: string[] = [];
  const expectedFields = ['uid', 'name', 'dateOfBirth', 'description', 'createdAt', 'location', 'answers', 'pictureUrl', 'beacon'];

  const field_errors = validateFieldsMatch(expectedFields, data);
  for (const err of field_errors) {
    errors.push(err);
  }

  if (data.uid && typeof data.uid !== 'string') {
    errors.push('uid must be a string');
  }

  if (data.name && typeof data.name !== 'string') {
    errors.push('name must be a string');
  }

  if (data.dateOfBirth && typeof data.dateOfBirth !== 'string') {
    errors.push('dateOfBirth must be a string');
  }

  if (data.description && typeof data.description !== 'string') {
    errors.push('description must be a string');
  }

  if (data.location !== null && data.location !== undefined) {
    if (typeof data.location !== 'object' || !('latitude' in data.location) || !('longitude' in data.location)) {
      errors.push('location must be an object with latitude and longitude, or null');
    } else {
      if (typeof data.location.latitude !== 'number' || typeof data.location.longitude !== 'number') {
        errors.push('location.latitude and location.longitude must be numbers');
      }
    }
  }

  if (data.answers && typeof data.answers !== 'object') {
    errors.push('answers must be an object');
  }

  if (data.pictureUrl && typeof data.pictureUrl !== 'string') {
    errors.push('pictureUrl must be a string');
  }

  if (data.beacon) {
    if (typeof data.beacon !== 'object') {
      errors.push('beacon must be an object');
    } else {
      const expectedCategories = ['love', 'habits', 'ambitions', 'spirituality', 'emotions'];
      for (const category of expectedCategories) {
        if (!(category in data.beacon)) {
          errors.push(`beacon.${category} is required`);
        } else if (!Array.isArray(data.beacon[category])) {
          errors.push(`beacon.${category} must be an array`);
        } else {
          for (let i = 0; i < data.beacon[category].length; i++) {
            const beaconItem = data.beacon[category][i];
            if (typeof beaconItem !== 'object' || !('text' in beaconItem) || !('type' in beaconItem)) {
              errors.push(`beacon.${category}[${i}] must have text and type fields`);
            } else {
              if (typeof beaconItem.text !== 'string') {
                errors.push(`beacon.${category}[${i}].text must be a string`);
              }
              if (beaconItem.type !== 'I' && beaconItem.type !== 'You' && beaconItem.type !== 'We') {
                errors.push(`beacon.${category}[${i}].type must be 'I', 'You', or 'We'`);
              }
              const beaconKeys = Object.keys(beaconItem);
              if (beaconKeys.length !== 2 || !beaconKeys.includes('text') || !beaconKeys.includes('type')) {
                errors.push(`beacon.${category}[${i}] must have exactly text and type fields`);
              }
            }
          }
        }
      }
      const extraCategories = Object.keys(data.beacon).filter(key => !expectedCategories.includes(key));
      if (extraCategories.length > 0) {
        errors.push(`beacon has unexpected categories: ${extraCategories.join(', ')}`);
      }
    }
  }

  // Validate beacons field (old format, for backward compatibility during migration)
  if (data.beacons) {
    if (typeof data.beacons !== 'object') {
      errors.push('beacons must be an object');
    } else {
      const expectedCategories = ['love', 'habits', 'ambitions', 'spirituality', 'emotions'];
      for (const category of expectedCategories) {
        if (!(category in data.beacons)) {
          errors.push(`beacons.${category} is required`);
        } else if (!Array.isArray(data.beacons[category])) {
          errors.push(`beacons.${category} must be an array`);
        } else {
          for (let i = 0; i < data.beacons[category].length; i++) {
            const beaconItem = data.beacons[category][i];
            if (typeof beaconItem !== 'object' || !('text' in beaconItem) || !('type' in beaconItem)) {
              errors.push(`beacons.${category}[${i}] must have text and type fields`);
            } else {
              if (typeof beaconItem.text !== 'string') {
                errors.push(`beacons.${category}[${i}].text must be a string`);
              }
              if (beaconItem.type !== 'I' && beaconItem.type !== 'You' && beaconItem.type !== 'We') {
                errors.push(`beacons.${category}[${i}].type must be 'I', 'You', or 'We'`);
              }
              const beaconKeys = Object.keys(beaconItem);
              if (beaconKeys.length !== 2 || !beaconKeys.includes('text') || !beaconKeys.includes('type')) {
                errors.push(`beacons.${category}[${i}] must have exactly text and type fields`);
              }
            }
          }
        }
      }
      const extraCategories = Object.keys(data.beacons).filter(key => !expectedCategories.includes(key));
      if (extraCategories.length > 0) {
        errors.push(`beacons has unexpected categories: ${extraCategories.join(', ')}`);
      }
    }
  }

  if (errors.length !== 0) {
    console.error(`profile validation errors:`, errors);
    throw new Error(`profile validation failed:\n${errors.join('\n')}`);
  }
}

// Yes/No Question validation
export function validateYesNoQuestion(id: string, data: any): void {
  const errors: string[] = [];
  const expectedFields = ['text', 'type', 'createdAt', 'category', 'class'];

  const field_errors = validateFieldsMatch(expectedFields, data);
  for (const err of field_errors) {
    errors.push(err);
  }

  // Type validation
  if (data.text && typeof data.text !== 'string') {
    errors.push('text must be a string');
  }

  if (data.type !== 'yes_no') {
    errors.push('type must be "yes_no"');
  }

  if (data.category !== undefined) {
    if (!Array.isArray(data.category)) {
      errors.push('category must be an array');
    } else {
      for (let i = 0; i < data.category.length; i++) {
        if (typeof data.category[i] !== 'string') {
          errors.push(`category[${i}] must be a string`);
        }
      }
    }
  }

  if (data.class !== undefined) {
    if (typeof data.class !== 'number' || !Number.isInteger(data.class) || data.class < 1) {
      errors.push('class must be a positive integer');
    }
  }

  if (errors.length !== 0) {
    console.error(`Yes/No question ${id} validation errors:`, errors);
    throw new Error(`Yes/No question ${id} validation failed:\n${errors.join('\n')}`);
  }
}

export function validateMultipleChoiceQuestion(id: string, data: any): void {
  const errors: string[] = [];
  const expectedFields = ['text', 'type', 'options', 'createdAt', 'category', 'class'];

  const field_errors = validateFieldsMatch(expectedFields, data);
  for (const err of field_errors) {
    errors.push(err);
  }

  // Type validation
  if (data.text && typeof data.text !== 'string') {
    errors.push('text must be a string');
  }

  if (data.type !== 'multiple_choice') {
    errors.push('type must be "multiple_choice"');
  }

  if (data.options) {
    if (!Array.isArray(data.options)) {
      errors.push('options must be an array');
    } else {
      for (let i = 0; i < data.options.length; i++) {
        if (typeof data.options[i] !== 'string') {
          errors.push(`options[${i}] must be a string`);
        }
      }
    }
  }

  if (data.category !== undefined) {
    if (!Array.isArray(data.category)) {
      errors.push('category must be an array');
    } else {
      for (let i = 0; i < data.category.length; i++) {
        if (typeof data.category[i] !== 'string') {
          errors.push(`category[${i}] must be a string`);
        }
      }
    }
  }

  if (data.class !== undefined) {
    if (typeof data.class !== 'number' || !Number.isInteger(data.class) || data.class < 1) {
      errors.push('class must be a positive integer');
    }
  }

  if (errors.length !== 0) {
    console.error(`Multiple choice question ${id} validation errors:`, errors);
    throw new Error(`Multiple choice question ${id} validation failed:\n${errors.join('\n')}`);
  }
}

export function validateYesNoAnswer(id:string, data: any): void {
  const errors: string[] = [];
  const expectedFields = ['myAnswer', 'partnerPreference', 'importance', 'mirrored', 'modifiedAt'];

  // Validate fields match (excluding optional note field for backward compatibility)
  const dataWithoutNote = { ...data };
  delete dataWithoutNote.note;
  const field_errors = validateFieldsMatch(expectedFields, dataWithoutNote);
  for (const err of field_errors) {
    errors.push(err);
  }

  // Type validation
  const validYesNoValues = ['No', "I don't think so", 'I think so', 'Yes'];

  if (data.myAnswer !== null) {
    if (typeof data.myAnswer !== 'string' || !validYesNoValues.includes(data.myAnswer)) {
      errors.push('myAnswer must be one of: "No", "I don\'t think so", "I think so", "Yes", or null');
    }
  }

  if (data.partnerPreference !== null) {
    if (typeof data.partnerPreference !== 'string' || !validYesNoValues.includes(data.partnerPreference)) {
      errors.push('partnerPreference must be one of: "No", "I don\'t think so", "I think so", "Yes", or null');
    }
  }

  if (data.importance !== null) {
    if (!['preference', 'important', 'dealbreaker'].includes(data.importance)) {
      errors.push('importance must be "preference", "important", "dealbreaker", or null');
    }
  }

  if (typeof data.mirrored !== 'boolean') {
    errors.push('mirrored must be a boolean');
  }

  if (typeof data.modifiedAt !== 'number') {
    errors.push('modifiedAt must be a timestamp (number)');
  }

  // Note field validation (allow undefined for backward compatibility)
  if (data.note !== undefined && data.note !== null) {
    if (typeof data.note !== 'string') {
      errors.push('note must be a string or null');
    } else if (data.note.length > 1000) {
      errors.push('note must not exceed 1000 characters');
    }
  }

  if (errors.length !== 0) {
    console.error(`Yes/No question ${id} validation errors:`, errors);
    throw new Error(`Yes/No question ${id} validation failed:\n${errors.join('\n')}`);
  }
}

// Multiple Choice Answer validation
export function validateMultipleChoiceAnswer(id: string, data: any): void {
  const errors: string[] = [];
  const expectedFields = ['myAnswer', 'partnerPreference', 'importance', 'mirrored', 'modifiedAt'];

  // Validate fields match (excluding optional note field for backward compatibility)
  const dataWithoutNote = { ...data };
  delete dataWithoutNote.note;
  const field_errors = validateFieldsMatch(expectedFields, dataWithoutNote);
  for (const err of field_errors) {
    errors.push(err);
  }

  // Type validation
  if (data.myAnswer !== undefined && data.myAnswer !== null) {
    if (!Array.isArray(data.myAnswer)) {
      errors.push('myAnswer must be an array or null');
    } else {
      for (let i = 0; i < data.myAnswer.length; i++) {
        if (typeof data.myAnswer[i] !== 'string') {
          errors.push(`myAnswer[${i}] must be a string`);
        }
      }
    }
  }

  if (data.partnerPreference !== undefined && data.partnerPreference !== null) {
    if (!Array.isArray(data.partnerPreference)) {
      errors.push('partnerPreference must be an array or null');
    } else {
      for (let i = 0; i < data.partnerPreference.length; i++) {
        if (typeof data.partnerPreference[i] !== 'string') {
          errors.push(`partnerPreference[${i}] must be a string`);
        }
      }
    }
  }

  if (data.importance !== undefined && data.importance !== null) {
    if (!['preference', 'important', 'dealbreaker'].includes(data.importance)) {
      errors.push('importance must be "preference", "important", "dealbreaker", or null');
    }
  }

  if (data.mirrored !== undefined && typeof data.mirrored !== 'boolean') {
    errors.push('mirrored must be a boolean');
  }

  if (data.modifiedAt !== undefined && typeof data.modifiedAt !== 'number') {
    errors.push('modifiedAt must be a timestamp (number)');
  }

  // Note field validation (allow undefined for backward compatibility)
  if (data.note !== undefined && data.note !== null) {
    if (typeof data.note !== 'string') {
      errors.push('note must be a string or null');
    } else if (data.note.length > 1000) {
      errors.push('note must not exceed 1000 characters');
    }
  }

  if (errors.length !== 0) {
    console.error(`Multiple choice answer for question ${id} validation errors:`, errors);
    throw new Error(`Multiple choice answer for question ${id} validation failed:\n${errors.join('\n')}`);
  }
}
